import { useEffect, useMemo, useState } from 'react'
import { Reorder, motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import BrandShell from '../components/BrandShell.jsx'
import LeaderboardRow from '../components/LeaderboardRow.jsx'
import ActivityTicker from '../components/ActivityTicker.jsx'
import GuidedTour from '../components/GuidedTour.jsx'
import BadgeUnlockModal from '../components/BadgeUnlockModal.jsx'
import HelpTooltip from '../components/HelpTooltip.jsx'
import PointsCounter from '../components/PointsCounter.jsx'
import StatGrid from '../components/StatGrid.jsx'
import PrizePool from '../components/PrizePool.jsx'
import BadgeShelf from '../components/BadgeShelf.jsx'
import HowToEarn from '../components/HowToEarn.jsx'
import ParticipantDetailModal from '../components/ParticipantDetailModal.jsx'
import { useRealtimeLeaderboard } from '../hooks/useRealtimeLeaderboard.js'
import { useParticipant } from '../hooks/useParticipant.js'
import { usePoints } from '../hooks/usePoints.js'
import { useBadgeUnlocks } from '../hooks/useBadgeUnlocks.js'
import { weeklyCapsRemaining, totalPoints } from '../lib/scoring.js'

export default function Leaderboard() {
  const { participant } = useParticipant()
  const { rows, status, lastUpdate } = useRealtimeLeaderboard()
  const { activities, events } = usePoints(participant?.id)
  const { pending, markSeen } = useBadgeUnlocks(participant?.id)
  const [filter, setFilter] = useState('quarter')
  const [previousRanks, setPreviousRanks] = useState({})
  const [detail, setDetail] = useState(null) // { participant, tab }

  // capture previous ranks so we can show the ▲▼ delta
  useEffect(() => {
    if (!rows.length) return
    setPreviousRanks(prev => {
      const next = {}
      rows.forEach(r => {
        next[r.participant.id] = r.currentRank
      })
      const changed = Object.keys(next).some(k => prev[k] !== next[k])
      return changed ? next : prev
    })
    // we intentionally only sync on rows change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map(r => r.participant.id + ':' + r.currentRank).join(',')])

  const enriched = useMemo(
    () => rows.map(r => ({ ...r, previousRank: previousRanks[r.participant.id] })),
    [rows, previousRanks]
  )

  const myPoints = totalPoints(events)
  const caps = useMemo(() => weeklyCapsRemaining(activities), [activities])

  return (
    <BrandShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1fr,320px] gap-6">
        <section data-tour="leaderboard">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
                Leaderboard
                <HelpTooltip label="How the leaderboard works">
                  Live point total for the quarter. Updates the moment something new lands. Top 2 plus best single post pay out on 6 August.
                </HelpTooltip>
              </h1>
              {lastUpdate && (
                <span className="text-xs text-brand-text-muted">
                  Updated {timeAgo(lastUpdate)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 bg-white border border-brand-border rounded-lg p-1 text-xs">
              {['week', 'month', 'quarter'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-md transition-colors ${
                    filter === f ? 'bg-brand-blue text-white' : 'text-brand-text-muted hover:text-brand-text'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {status === 'loading' && <LeaderboardSkeleton />}

          {status === 'ready' && enriched.length === 0 && (
            <EmptyState />
          )}

          {status === 'ready' && enriched.length > 0 && (
            <Reorder.Group
              axis="y"
              values={enriched}
              onReorder={() => { /* readonly */ }}
              className="space-y-3"
            >
              {enriched.map(row => (
                <LeaderboardRow
                  key={row.participant.id}
                  row={row}
                  isMe={row.participant.id === participant?.id}
                  onClick={() => setDetail({ participant: row.participant, tab: 'posts' })}
                />
              ))}
            </Reorder.Group>
          )}
        </section>

        <aside className="flex flex-col gap-4 lg:h-full" data-tour="dashboard">
          <div className="bg-white border border-brand-border rounded-xl p-5 shadow-card">
            <button
              type="button"
              onClick={() => participant && setDetail({ participant, tab: 'points' })}
              className="block w-full text-left group"
              title="Click to see where these points came from"
            >
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-brand-text-muted">Your points</div>
                <span className="text-[10px] text-brand-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap for breakdown ↗
                </span>
              </div>
              <PointsCounter
                value={myPoints}
                soundOnIncrease
                className="text-4xl font-bold text-brand-text group-hover:text-brand-blue transition-colors"
              />
            </button>
            <div className="mt-5 pt-4 border-t border-brand-border" data-tour="caps">
              <StatGrid streak={computeStreak(activities)} caps={caps} />
            </div>

            <Link
              to="/log"
              data-tour="log-cta"
              className="mt-5 block w-full text-center bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-3 rounded-lg transition-colors"
            >
              + Log activity
            </Link>
            <p className="text-xs text-brand-text-muted text-center mt-2">
              Paste a LinkedIn URL. The rest is automatic.
            </p>
          </div>

          <PrizePool />

          <HowToEarn />

          <div className="bg-white border border-brand-border rounded-xl p-5 shadow-card lg:flex-1" data-tour="badges">
            <BadgeShelf
              unlockedSlugs={(participant?.badge_slugs || [])}
            />
          </div>
        </aside>
      </div>

      <ActivityTicker />
      <GuidedTour />
      <BadgeUnlockModal
        badge={pending[0]}
        onDismiss={() => pending[0] && markSeen(pending[0].id)}
      />
      {detail && (
        <ParticipantDetailModal
          participant={detail.participant}
          initialTab={detail.tab}
          onClose={() => setDetail(null)}
        />
      )}
    </BrandShell>
  )
}

function LeaderboardSkeleton() {
  return (
    <div className="space-y-3">
      {[1,2,3,4,5,6].map(i => (
        <div key={i} className="skeleton h-20" />
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-brand-border rounded-xl p-12 text-center">
      <div className="text-4xl mb-3">🌅</div>
      <h3 className="text-lg font-semibold text-brand-text">First in.</h3>
      <p className="text-sm text-brand-text-muted mt-1">No one has scored yet. Log something and take the top spot.</p>
      <Link to="/log" className="inline-block mt-4 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-blue-hover">
        Log activity →
      </Link>
    </div>
  )
}

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

function computeStreak(activities) {
  if (!activities.length) return 0
  const days = new Set(
    activities
      .filter(a => a.content_gate_passed)
      .map(a => a.created_at.slice(0, 10))
  )
  let streak = 0
  const cursor = new Date()
  while (days.has(cursor.toISOString().slice(0, 10))) {
    streak++
    cursor.setUTCDate(cursor.getUTCDate() - 1)
  }
  return streak
}
